/*global google*/
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  LoadingOverlay,
  MultiSelect,
  Paper,
  Select,
  Title,
} from '@mantine/core';
import type { PlaceWithServices, PublishStatus } from '../models/Place.ts';
import {
  IconCaretLeft,
  IconCaretRight,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { AddPlaceModal } from '../components/modal/places/AddPlaceModal.tsx';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { GetPlacesByBoundingBoxRequest } from '../api/requests/place-request.ts';
import useSearchPlacesByBoundingBox from '../hooks/places/useSearchPlacesByBoundingBox.ts';
import { useDebouncedCallback } from '@mantine/hooks';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import MapPins from '../components/ui/places/MapPin.tsx';
import { useTheme } from '../context/ThemeProvider.tsx';
import NearbyPlacesList from '../components/ui/places/NearbyPlacesList.tsx';
import KeywordsSearchModal from '../components/modal/places/KeywordsSearchModal.tsx';
import SetMapCenterByLatLngValuePopover from '../components/ui/places/SetMapCenterByLatLngValuePopover.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import usePlace from '../hooks/places/usePlace.ts';
import { publishPlace } from '../api/place.ts';

const DEFAULT_ZOOM_LEVEL = 15; // 地圖瀏覽的 default zoom level
const DEFAULT_CENTER_SELECTED_ZOOM = 16; // 選定地點後的 default zoom level
const DEFAULT_CENTER = { lat: 25.042028649911156, lng: 121.51736237310261 }; // 地圖的初始中心位置
const DEFAULT_ZOOM_LEVEL_LAT_LNG_RANGE = {
  latRange: 0.02797143054,
  lngRange: 0.06708505067,
}; // 地圖的初始顯示範圍

// 以當前地圖的中心, lat lng range (取決於zoom level), limit, 和服務類型, 包裝成 getPlacesByBoundingBox 的 request 格式
const getPlacesRequestWithSelectedCenter = (
  centerLat: number,
  centerLng: number,
  latRange: number,
  lngRange: number,
  limit?: number,
  serviceTypes?: string[],
): GetPlacesByBoundingBoxRequest => {
  return {
    minLat: centerLat - latRange / 2,
    minLng: centerLng - lngRange / 2,
    maxLat: centerLat + latRange / 2,
    maxLng: centerLng + lngRange / 2,
    limit: limit || 200,
    serviceTypes: serviceTypes,
  };
};

// 地圖剛加載出來時的 default getPlacesByBoundingBoxRequest
const initialRequest: GetPlacesByBoundingBoxRequest =
  getPlacesRequestWithSelectedCenter(
    DEFAULT_CENTER.lat,
    DEFAULT_CENTER.lng,
    DEFAULT_ZOOM_LEVEL_LAT_LNG_RANGE.latRange,
    DEFAULT_ZOOM_LEVEL_LAT_LNG_RANGE.lngRange,
    200,
    [],
  );

const useMapEvent = ({
  onBoundsChange,
  setHasUserMovedMap,
}: {
  onBoundsChange?: (bounds: {
    minLat: number;
    minLng: number;
    maxLat: number;
    maxLng: number;
  }) => void;
  setHasUserMovedMap: (hasUserMoved: boolean) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !onBoundsChange) return;

    // 監聽地圖移動停止事件
    const listener = map.addListener('idle', () => {
      const bounds = map.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        onBoundsChange({
          minLat: sw.lat(),
          minLng: sw.lng(),
          maxLat: ne.lat(),
          maxLng: ne.lng(),
        });
      }
    });

    // 清理監聽器
    return () => {
      if (listener) {
        google.maps.event.removeListener(listener);
      }
    };
  }, [map, onBoundsChange]);

  useEffect(() => {
    if (!map) return;

    // 監聽使用者手動移動地圖, 包含 拖曳 與 縮放
    // 只有在非程式控制移動時才標記為使用者移動
    const dragEndListener = map.addListener('dragend', () => {
      setHasUserMovedMap(true);
    });

    const zoomChangedListener = map.addListener('zoom_changed', () => {
      setHasUserMovedMap(true);
    });

    return () => {
      google.maps.event.removeListener(dragEndListener);
      google.maps.event.removeListener(zoomChangedListener);
    };
  }, [map, setHasUserMovedMap]);
};

// 主地圖頁面
const MapContainer: React.FC = () => {
  const map = useMap();

  const hasUserMovedMapRef = React.useRef(false);
  const setUserMovedRef = (value: boolean) => {
    hasUserMovedMapRef.current = value;
  };
  const navigate = useNavigate();
  const location = useLocation();
  const incomingPlaceId = (
    location.state as { selectedLocation?: string } | null
  )?.selectedLocation;

  const [searchServiceTypes, setSearchServiceTypes] = useState<string[]>([]); // filter 的服務類型(多選)
  const [searchPublishStatus, setSearchPublishStatus] =
    useState<PublishStatus | null>(null); // filter 的上下架狀態
  const [selectedLocation, setSelectedLocation] =
    useState<PlaceWithServices | null>(null); // 使用者選定的地點

  // 從 PlaceInformationPage 返回時，恢復選定場站
  const { data: incomingPlace } = usePlace(incomingPlaceId ?? '');
  useEffect(() => {
    if (!incomingPlace || !map) return;
    setSelectedLocation(incomingPlace);
    map.setCenter({
      lat: incomingPlace.location.lat,
      lng: incomingPlace.location.lng,
    });
    if ((map.getZoom() ?? 0) < DEFAULT_CENTER_SELECTED_ZOOM) {
      map.setZoom(DEFAULT_CENTER_SELECTED_ZOOM);
    }

    // 等地圖完全穩定（zoom 動畫 + tile 載入結束）後再 panBy，
    // 避免 panBy 被地圖初始渲染流程覆蓋
    const onIdle = map.addListener('idle', () => {
      google.maps.event.removeListener(onIdle);
      map.panBy(-170, 0);
      hasUserMovedMapRef.current = false;
    });
  }, [incomingPlace, map]);

  const [searchLatLngValue, setSearchLatLngValue] = useState<string>(''); // 使用者輸入的經緯度查詢值
  const [isNearbyPlacesListOpen, setIsNearbyPlacesListOpen] = useState(false); // 附近場站列表的開啟／收和狀態
  const queryClient = useQueryClient();

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false); // 控制搜尋 Modal 開關
  const [
    addPlaceModalOpened,
    { open: openAddPlaceModal, close: closeAddPlaceModal },
  ] = useDisclosure(false);

  // GetPlacesByBoundingBox: 根據地圖顯示範圍, 取回圖資資料
  // 範圍（地圖平移/縮放）跟服務類型篩選分開存，實際送出的 query 用 useMemo
  // 即時合併兩者——不管哪一個變了都會自動重打 API，範圍變動也不會洗掉篩選。
  const [currentBounds, setCurrentBounds] = useState<{
    minLat: number;
    minLng: number;
    maxLat: number;
    maxLng: number;
  }>({
    minLat: initialRequest.minLat,
    minLng: initialRequest.minLng,
    maxLat: initialRequest.maxLat,
    maxLng: initialRequest.maxLng,
  });

  const currentQuery = useMemo<GetPlacesByBoundingBoxRequest>(
    () => ({
      ...currentBounds,
      limit: 200,
      serviceTypes: searchServiceTypes.length ? searchServiceTypes : null,
    }),
    [currentBounds, searchServiceTypes],
  );

  const {
    data: places,
    isLoading: isPlacesLoading,
    error: getPlacesByBoundingBoxError,
  } = useSearchPlacesByBoundingBox(currentQuery);

  // 篩選後的位置數據 (服務類型 以及 上下架狀態)
  // 其中 服務類型是多選, 且為所有選項的聯集
  const filteredPlaces = useMemo(() => {
    if (!places) return [];

    return places.filter((place) => {
      const matchServiceType = searchServiceTypes.length
        ? searchServiceTypes.every((selectedType) =>
            place.services.some((s) => s.serviceType === selectedType),
          )
        : true;

      // 沒有主動篩選狀態時，預設排除已下架地點——關鍵字搜尋（KeywordsSearchModal）
      // 是唯一還能讓已下架地點出現的路徑，這裡故意不管。
      const matchPublishStatus = searchPublishStatus
        ? place.publishStatus === searchPublishStatus
        : place.publishStatus !== 'UNPUBLISHED';

      return matchServiceType && matchPublishStatus;
    });
  }, [places, searchServiceTypes, searchPublishStatus]);

  // 新增圖資成功後的操作：關閉modal -> 顯示notification -> refetch -> 跳轉到資訊展開頁
  const handlePlaceAdded = useCallback(
    async (placeId: string) => {
      closeAddPlaceModal();
      notifySuccess('新增地點成功');
      await queryClient.invalidateQueries({
        queryKey: ['places'],
        exact: false,
      });
      await publishPlace(placeId);
      navigate(`/places/${placeId}`);
    },
    [navigate, queryClient, closeAddPlaceModal],
  );

  // 每次地圖的 bounds 變更後, 重新發送 get places request
  // （serviceTypes 由上面的 currentQuery useMemo 一併帶入，這裡不用管）
  const handleBoundsChange = useDebouncedCallback(
    (bounds: {
      minLat: number;
      minLng: number;
      maxLat: number;
      maxLng: number;
    }) => {
      setCurrentBounds(bounds);
    },
    350,
  );

  useMapEvent({
    onBoundsChange: handleBoundsChange,
    setHasUserMovedMap: (hasUserMoved: boolean) => {
      hasUserMovedMapRef.current = hasUserMoved;
    },
  });

  const handleNearbyPlacesListToggleClick = () => {
    const newStatus = !isNearbyPlacesListOpen; // 為了應付 React 的非同步更新, 所以要這樣做

    setIsNearbyPlacesListOpen(newStatus);

    if (selectedLocation) {
      if (newStatus) {
        if (!hasUserMovedMapRef.current) {
          map.panBy(-200, 0);
        }
      } else {
        if (!hasUserMovedMapRef.current) {
          map.setCenter({
            lat: selectedLocation.location.lat,
            lng: selectedLocation.location.lng,
          });
          map.panBy(-170, 0);
        }
      }
    }
  };

  // 使用者選定的 服務類型
  const handleServiceTypeChange = useCallback((value: string[]) => {
    setSearchServiceTypes(value);
  }, []);

  // 使用者選定的 上下架狀態
  const handleSearchPublishStatusChange = useCallback((value: string) => {
    setSearchPublishStatus(value as PublishStatus);
  }, []);

  // 處理 新增圖資 按鈕按下的操作 -> 開啟 modal
  const handleAddPlaceClick = useCallback(() => {
    openAddPlaceModal();
  }, [openAddPlaceModal]);

  const { colorScheme } = useTheme();

  // 載入畫面時的 Loading Overlays 以及 Error
  if (!places && isPlacesLoading) {
    return (
      <>
        <LoadingOverlay
          visible={true}
          overlayProps={{ radius: 'sm', blur: 1 }}
          loaderProps={{
            color: 'rgb(67,154,255)',
            type: 'bars',
            size: 'sm',
          }}
        />
      </>
    );
  }
  if (getPlacesByBoundingBoxError)
    return <div style={{ marginLeft: 'xl' }}>取得圖資時發生錯誤</div>;

  // 從 useTheme 取得 light / dark mode 資訊
  const getComponentColorScheme = (
    scheme: 'light' | 'dark' | 'auto',
  ): 'LIGHT' | 'DARK' => {
    if (scheme === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'DARK'
        : 'LIGHT';
    }
    return scheme === 'dark' ? 'DARK' : 'LIGHT';
  };

  return (
    <>
      {addPlaceModalOpened && (
        <AddPlaceModal
          opened={addPlaceModalOpened}
          onClose={closeAddPlaceModal}
          onSuccess={handlePlaceAdded}
        />
      )}

      <Paper shadow="xl" radius="lg" pb={20}>
        {/* 頁面標題和操作按鈕 */}
        <Group
          justify="space-between"
          p="24px 24px 20px 24px"
          style={{ borderBottom: 'none', flexShrink: 0, minWidth: 988 }}
        >
          <Title order={2}>圖資管理</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            size={'md'}
            variant="filled"
            onClick={handleAddPlaceClick}
          >
            新增圖資
          </Button>
        </Group>

        {/* 地圖區域 */}
        <div
          style={{
            flex: '1 1 0%',
            display: 'flex',
            marginLeft: 24,
            marginRight: 24,
            height: '82vh',
            overflow: 'hidden',
            position: 'relative',
            minWidth: 940,
          }}
        >
          {/* 地圖本身 */}
          <div
            style={{
              flex: '1 1 0%',
              position: 'absolute',
              top: '0',
              left: '0',
              bottom: '0',
              right: '0',
            }}
          >
            <div
              style={{
                height: 'auto',
                width: 'auto',
                zIndex: 0,
                position: 'relative',
                borderRadius: 20,
                border:
                  colorScheme === 'light'
                    ? '1px solid #dee2e6'
                    : '1px solid #737373ff',
                overflow: 'hidden',
              }}
            >
              <Map
                mapId={'DEMO_MAP_ID'}
                style={{
                  width: '100%',
                  height: '82vh',
                  borderRadius: '20px',
                }}
                defaultCenter={DEFAULT_CENTER}
                defaultZoom={DEFAULT_ZOOM_LEVEL}
                minZoom={13}
                gestureHandling="greedy"
                disableDefaultUI={true}
                zoomControl={true}
                streetViewControl={false}
                mapTypeControl={false}
                fullscreenControl={false}
                colorScheme={getComponentColorScheme(colorScheme)}
              >
                <MapPins
                  key={searchLatLngValue}
                  places={filteredPlaces}
                  selectedLocation={selectedLocation}
                  searchServiceType={searchServiceTypes}
                  searchLatLngValue={searchLatLngValue}
                  setSelectedLocation={setSelectedLocation}
                  isNearbyPlacesListOpen={isNearbyPlacesListOpen}
                  defaultCenterSelectedZoom={DEFAULT_CENTER_SELECTED_ZOOM}
                  setHasUserMovedMap={setUserMovedRef}
                />
              </Map>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {/* 上方列: 搜尋與篩選 */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: 0,
                marginTop: 20,
                paddingInline: 38,
                paddingBottom: 20,
                minWidth: 988,
                overflow: 'hidden',
              }}
            >
              <Group align={'start'} gap={20} h={36}>
                {/* 搜尋按鈕 */}
                <Button
                  variant="default"
                  color={getComponentColorScheme(colorScheme)}
                  onClick={() => setIsSearchModalOpen(true)}
                  styles={{
                    root: {
                      width: 380,
                      height: 36,
                      justifyContent: 'flex-start',
                      fontWeight: 400,
                      color: '#495057',
                      borderRadius: 4,
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                    inner: {
                      justifyContent: 'flex-start',
                      paddingLeft: 9,
                    },
                  }}
                >
                  <Group gap={8}>
                    <IconSearch size={16} color="#adb5bd" />
                    <div
                      style={{
                        color: colorScheme === 'light' ? '#adb5bd' : '#696969',
                        fontSize: 14,
                      }}
                    >
                      請輸入地點名稱、地址或服務名稱
                    </div>
                  </Group>
                </Button>

                {/* 搜尋介面 */}
                {isSearchModalOpen && (
                  <KeywordsSearchModal
                    setIsSearchModalOpen={setIsSearchModalOpen}
                    setSelectedLocation={setSelectedLocation}
                    isNearbyPlacesListOpen={isNearbyPlacesListOpen}
                    defaultCenterSelectedZoom={DEFAULT_CENTER_SELECTED_ZOOM}
                    setHasUserMovedMap={setUserMovedRef}
                  />
                )}

                {/* 篩選服務類型(多選) */}
                <MultiSelect
                  placeholder={
                    searchServiceTypes.length ? '' : '請選擇服務類型'
                  }
                  data={[
                    { value: 'PARKING', label: '停車場', disabled: false },
                    { value: 'FUELING', label: '加油站', disabled: false },
                    { value: 'CHARGING', label: '充電站', disabled: false },
                    { value: 'MAINTENANCE', label: '保修廠', disabled: false },
                    { value: 'INSPECTION', label: '驗車場', disabled: false },
                  ]}
                  value={searchServiceTypes}
                  onChange={handleServiceTypeChange}
                  clearable
                  style={{ width: 280, height: 40, fontSize: 'md' }}
                />
                {/* 篩選上下架狀態 */}
                <Select
                  placeholder={searchPublishStatus ? '' : '請選擇地點狀態'}
                  data={[
                    { value: 'PUBLISHED', label: '已上架' },
                    { value: 'PENDING', label: '待上架' },
                    { value: 'UNPUBLISHED', label: '已下架' },
                  ]}
                  value={searchPublishStatus}
                  onChange={handleSearchPublishStatusChange}
                  clearable
                  style={{ width: 240, height: 40, fontSize: 'md' }}
                />
                {/*/!* 經緯度搜尋按鈕 *!/*/}
                <SetMapCenterByLatLngValuePopover
                  setSearchLatLngValue={setSearchLatLngValue}
                  defaultCenterSelectedZoom={DEFAULT_CENTER_SELECTED_ZOOM}
                />
              </Group>

              {isPlacesLoading && <Loader color={'blue'} size={36} />}
            </div>

            {/* 附近場站列表 & 收合鈕 */}
            <div
              style={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'row',
                left: 0,
                top: 80,
                bottom: 0,
              }}
            >
              {/* 列表收合鈕 */}
              <div
                style={{
                  position: 'relative',
                  left: 0,
                  top: 0,
                  height: 584,
                  width: 30,
                  alignContent: 'center',
                }}
              >
                <ActionIcon
                  variant="default"
                  onClick={() => handleNearbyPlacesListToggleClick()}
                  style={{
                    height: 139,
                    width: 24,
                    fontSize: 12,
                    padding: 5,
                    border: '0.5px solid',
                    borderColor:
                      colorScheme === 'light' ? '#dee2e6' : '#737373ff',
                    borderRadius: '0px 20px 20px 0px',
                  }}
                >
                  {isNearbyPlacesListOpen ? (
                    <IconCaretLeft
                      color={colorScheme === 'light' ? '#afafafff' : '#ced4da'}
                    />
                  ) : (
                    <IconCaretRight
                      color={colorScheme === 'light' ? '#afafafff' : '#ced4da'}
                    />
                  )}
                </ActionIcon>
              </div>

              {/* 附近場站列表 */}
              {isNearbyPlacesListOpen && (
                <NearbyPlacesList
                  places={filteredPlaces}
                  isPlacesLoading={isPlacesLoading}
                  selectedLocation={selectedLocation}
                  setSelectedLocation={setSelectedLocation}
                  isNearbyPlacesListOpen={isNearbyPlacesListOpen}
                  defaultCenterSelectedZoom={DEFAULT_CENTER_SELECTED_ZOOM}
                  setHasUserMovedMap={setUserMovedRef}
                />
              )}
            </div>
          </div>
        </div>
      </Paper>
    </>
  );
};

const MapSearchPage: React.FC = () => {
  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAP_API_KEY}>
      <MapContainer />
    </APIProvider>
  );
};

export default MapSearchPage;
